import { useCallback, useMemo } from 'react';

import { apiPatch } from '../lib/api_v2';
import { storeDirectMessagePayload } from '../lib/direct_messages_store';
import { logError } from '../lib/logger';

import { useRouteWorkspace } from '../contexts/RouteWorkspaceContext';

// The DM's background lives on its hidden workspace, which participants can't PATCH
// directly; saves go through the DM endpoint instead. Shared by every surface that
// renders WorkspaceBackgroundPicker on a DM route (preferences modal, onboarding deck):
// pass `backgroundResource` as the picker's workspace and the two savers as overrides.
export default function useDirectMessageBackground() {
  const { directMessage, directMessageId, workspaceId } = useRouteWorkspace();

  const updateBackground = useCallback(
    async (attributes) => {
      if (!directMessageId) throw new Error('save_failed');

      const response = await apiPatch(`/direct_messages/${directMessageId}`, { direct_message: attributes });
      const data = await response.json();

      if (!(response.ok && data.ok === true)) {
        logError('useDirectMessageBackground', 'Failed to save background image', {
          errors: data.errors || data.validations,
        });
        throw new Error('save_failed');
      }

      const updatedDirectMessage = data.data?.direct_message;
      if (updatedDirectMessage?.id) {
        await storeDirectMessagePayload(updatedDirectMessage);
      }
      return updatedDirectMessage || null;
    },
    [directMessageId],
  );

  const saveAttachment = useCallback(
    async (attachmentId) => {
      const updatedDirectMessage = await updateBackground({ background_image_id: attachmentId || '' });
      return updatedDirectMessage?.background_image || null;
    },
    [updateBackground],
  );

  const savePreset = useCallback((preset) => updateBackground({ background_image_preset: preset || '' }), [updateBackground]);

  // The picker takes a workspace-shaped resource; the DM carries the same background keys.
  const backgroundResource = useMemo(
    () => ({
      id: workspaceId,
      background_image: directMessage?.background_image || null,
      background_image_preset: directMessage?.background_image_preset || null,
      background_image_url: directMessage?.background_image_url || null,
    }),
    [directMessage?.background_image, directMessage?.background_image_preset, directMessage?.background_image_url, workspaceId],
  );

  return { backgroundResource, saveAttachment, savePreset };
}
